import type { QuoteResponse } from "@defuse-protocol/one-click-sdk-typescript"
import { create } from "zustand"
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware"

// Use a no-op outside browser storage contexts.
const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}

const getPersistStorage = (): StateStorage => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage
    }
  } catch {
    // Ignore storage access failures.
  }
  return noopStorage
}

/** Stores write-once, signed non-dry quotes by deposit address for disputes. */
type State = {
  quotes: Record<string, QuoteResponse>
}

type Actions = {
  saveQuote: (quote: QuoteResponse & { appFee?: unknown }) => void
}

type Store = State & Actions

export const useOneClickQuotesStore = create<Store>()(
  persist(
    (set) => ({
      quotes: {},
      saveQuote: ({ appFee: _appFee, ...quoteResponse }) => {
        const depositAddress = quoteResponse.quote.depositAddress
        if (depositAddress == null) return
        set((state) => ({
          quotes: { ...state.quotes, [depositAddress]: quoteResponse },
        }))
      },
    }),
    {
      name: "one_click_quotes",
      storage: createJSONStorage(getPersistStorage),
    }
  )
)

/** Store a successful quote result; callers control when persistence occurs. */
export function saveQuoteFromResult(
  result: { ok: QuoteResponse & { appFee?: unknown } } | { err: string } | null
): void {
  if (result == null || !("ok" in result)) return
  useOneClickQuotesStore.getState().saveQuote(result.ok)
}
